import type {
  ChordAttempt,
  SessionSummary,
  TrainingMode,
  TransitionSample,
} from '@/types/training';
import { mean } from '@/audio/dsp/stats';

export interface SummaryInput {
  mode: TrainingMode;
  startedAt: number;
  durationSec: number;
  chordIds: string[];
  bpmStart: number;
  bpmEnd: number;
  attempts: ChordAttempt[];
  transitions: TransitionSample[];
  rhythmScore: number | null;
  clarityScore: number | null;
}

/**
 * Build the record that is persisted and shown on the completion screen.
 *
 * `uncertain` attempts are counted but excluded from accuracy: an attempt the
 * analyser could not hear is not a mistake by the player, and letting it lower
 * the score would make a noisy room look like bad playing.
 */
export function summarize(input: SummaryInput): SessionSummary {
  const correct = input.attempts.filter((a) => a.verdict === 'correct').length;
  const almost = input.attempts.filter((a) => a.verdict === 'almost').length;
  const wrong = input.attempts.filter((a) => a.verdict === 'wrong').length;
  const uncertain = input.attempts.filter((a) => a.verdict === 'uncertain').length;
  const judged = correct + almost + wrong;

  const measured = input.transitions
    .map((t) => t.seconds)
    .filter((s): s is number => s !== null);

  const confidences = input.attempts.filter((a) => a.verdict !== 'uncertain').map((a) => a.confidence);

  return {
    id: `${input.startedAt}-${input.mode}`,
    startedAt: input.startedAt,
    mode: input.mode,
    durationSec: input.durationSec,
    chordIds: input.chordIds,
    bpmStart: input.bpmStart,
    bpmEnd: input.bpmEnd,
    attempts: input.attempts.length,
    correct,
    almost,
    wrong,
    uncertain,
    avgConfidence: confidences.length > 0 ? mean(confidences) : 0,
    accuracy: judged > 0 ? Math.round(((correct + almost * 0.5) / judged) * 100) : 0,
    rhythmScore: input.rhythmScore,
    clarityScore: input.clarityScore,
    avgTransitionSec: measured.length > 0 ? mean(measured) : null,
    bestTransitionKey: bestTransition(input.transitions),
    transitions: input.transitions,
  };
}

/** The ordered transition with the fastest measured time in this session. */
function bestTransition(transitions: TransitionSample[]): string | null {
  let best: TransitionSample | null = null;
  for (const sample of transitions) {
    if (sample.seconds === null || !sample.success) continue;
    if (!best || best.seconds === null || sample.seconds < best.seconds) best = sample;
  }
  return best?.key ?? null;
}

/** `C>G` → `C → G`, for display. */
export function formatTransitionKey(key: string): string {
  const [from, to] = key.split('>');
  return `${from} → ${to}`;
}
